import { useState } from 'react'
import { Modal, TextInput, Select, MultiSelect, Group, Stack, Text } from '@mantine/core'
import { IconDeviceFloppy } from '@tabler/icons-react'
import { useArcTranslation } from 'hooks'
import { Button } from 'components/Button'
import { AddGroupModal } from '../../TeacherDashboard/components/ReviewStudentRequests/AddGroupModal'
import dayjs from 'dayjs'
import type { GradeOption, GroupOption } from 'api/arcMosaico/types'
import type { ManagedFaculty } from '../types'

const naturalSort = (a: string, b: string) =>
  a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })

interface Props {
  faculty: ManagedFaculty | null
  grades: GradeOption[]
  groups: GroupOption[]
  onClose: () => void
  onSave: (data: {
    firstName: string
    lastName: string
    role: string
    gradeIds: string[]
    groupIds: string[]
  }) => Promise<void>
  onAddGroup: (name: string) => Promise<GroupOption>
}

export function EditFacultyModal({ faculty, grades, groups, onClose, onSave, onAddGroup }: Props) {
  const { t } = useArcTranslation()
  const [firstName, setFirstName] = useState(faculty?.firstName ?? '')
  const [lastName, setLastName] = useState(faculty?.lastName ?? '')
  const [role, setRole] = useState<string | null>(faculty?.role ?? null)
  const [gradeIds, setGradeIds] = useState<string[]>(faculty?.grades?.map(g => g.id) ?? [])
  const [groupIds, setGroupIds] = useState<string[]>(faculty?.groups?.map(g => g.id) ?? [])
  const [loading, setLoading] = useState(false)
  const [addGroupOpened, setAddGroupOpened] = useState(false)
  const [addGroupLoading, setAddGroupLoading] = useState(false)

  const opened = faculty !== null

  const handleClose = () => {
    onClose()
  }

  const handleSave = async () => {
    if (!role) return
    setLoading(true)
    try {
      await onSave({ firstName, lastName, role, gradeIds, groupIds })
    } finally {
      setLoading(false)
    }
  }

  const roleOptions = [
    { value: 'teacher', label: t('edit_faculty_modal.role_teacher') },
    { value: 'coordinator', label: t('edit_faculty_modal.role_coordinator') },
  ]
  const gradeOptions = grades
    .map(g => ({ value: g.id, label: g.name }))
    .sort((a, b) => naturalSort(a.label, b.label))

  const groupOptions = [
    { value: '__create__', label: `+ ${t('student_requests.create_group')}` },
    ...groups
      .map(g => ({ value: g.id, label: g.name }))
      .sort((a, b) => naturalSort(a.label, b.label)),
  ]

  const handleGroupChange = (values: string[]) => {
    if (values.includes('__create__')) {
      setAddGroupOpened(true)
      return
    }
    setGroupIds(values)
  }

  const handleAddGroup = async (name: string): Promise<boolean> => {
    setAddGroupLoading(true)
    try {
      await onAddGroup(name)
      return true
    } catch {
      return false
    } finally {
      setAddGroupLoading(false)
    }
  }

  return (
    <>
    <Modal
      opened={opened}
      onClose={handleClose}
      title={t('edit_faculty_modal.title')}
      size='md'
      centered
    >
      <Stack spacing='sm'>
        <Group grow>
          <TextInput
            label={t('edit_faculty_modal.first_name')}
            value={firstName}
            onChange={e => setFirstName(e.currentTarget.value)}
            required
          />
          <TextInput
            label={t('edit_faculty_modal.last_name')}
            value={lastName}
            onChange={e => setLastName(e.currentTarget.value)}
            required
          />
        </Group>

        <Group grow>
          <div>
            <Text size='sm' weight={500}>{t('edit_faculty_modal.email')}</Text>
            <Text size='sm' color='dimmed'>{faculty?.email || '—'}</Text>
          </div>
          <div>
            <Text size='sm' weight={500}>{t('edit_faculty_modal.registered_at')}</Text>
            <Text size='sm' color='dimmed'>
              {faculty?.createdAt ? dayjs(faculty.createdAt).format('MM/DD/YYYY') : '—'}
            </Text>
          </div>
        </Group>

        <Select
          label={t('edit_faculty_modal.role')}
          data={roleOptions}
          value={role}
          onChange={setRole}
          placeholder={t('edit_faculty_modal.select_role')}
          required
        />

        <MultiSelect
          label={t('edit_faculty_modal.groups')}
          data={groupOptions}
          value={groupIds}
          onChange={handleGroupChange}
          placeholder={t('edit_faculty_modal.select_groups')}
          searchable
          required
        />

        <MultiSelect
          label={t('edit_faculty_modal.grades')}
          data={gradeOptions}
          value={gradeIds}
          onChange={setGradeIds}
          placeholder={t('edit_faculty_modal.select_grades')}
          required
        />

        <Group position='right' mt='md'>
          <Button variant='default' onClick={handleClose}>
            {t('edit_faculty_modal.cancel')}
          </Button>
          <Button
            color='turquoise'
            leftIcon={<IconDeviceFloppy size={16} />}
            onClick={handleSave}
            loading={loading}
          >
            {t('edit_faculty_modal.save')}
          </Button>
        </Group>
      </Stack>
    </Modal>

    <AddGroupModal
      opened={addGroupOpened}
      onClose={() => setAddGroupOpened(false)}
      onSave={handleAddGroup}
      loading={addGroupLoading}
    />
    </>
  )
}
